import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { warehouseApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';
import type {
  DashboardStats,
  Item,
  ItemModel,
  OwnerOption,
  Rack,
  Supplier,
  WarehouseLocation,
  WarehouseProcess,
  ProcessItemsResponse,
} from '@/api/capacities-types';

export * from '@/api/capacities-types';

/**
 * Capacities & Resources API (legacy `Warehouse`). Talks to the SEPARATE
 * warehouse service via `warehouseApi` (plain access token, base
 * `…/api/v2`). List envelope `{results, pagination}`; single `{result}`.
 * Read-only hooks — live create/edit/delete is performed by the user, not the app.
 */

export interface ListParams {
  page?: number;
  page_size?: number;
  search?: string;
  [key: string]: unknown;
}

const qstr = (params?: ListParams) =>
  params && Object.keys(params).length
    ? `?${qs.stringify(params, { skipNull: true, skipEmptyString: true })}`
    : '';

export const capacityKeys = {
  dashboard: ['capacities', 'dashboard'] as const,
  suppliers: (p?: ListParams) => ['capacities', 'suppliers', p] as const,
  supplier: (id: string) => ['capacities', 'supplier', id] as const,
  models: (p?: ListParams) => ['capacities', 'item-models', p] as const,
  model: (id: string) => ['capacities', 'item-model', id] as const,
  locations: (p?: ListParams) => ['capacities', 'locations', p] as const,
  location: (id: string) => ['capacities', 'location', id] as const,
  racks: (p?: ListParams) => ['capacities', 'racks', p] as const,
  rack: (id: string) => ['capacities', 'rack', id] as const,
  items: (p?: ListParams) => ['capacities', 'items', p] as const,
  item: (id: string) => ['capacities', 'item', id] as const,
  owners: ['capacities', 'owners'] as const,
  processes: (p?: ListParams) => ['capacities', 'processes', p] as const,
  process: (id: string) => ['capacities', 'process', id] as const,
};

async function getList<T>(path: string, params?: ListParams) {
  const { data } = await warehouseApi.get<ResponseArray<T>>(`${path}${qstr(params)}`);
  return data;
}
async function getOne<T>(path: string) {
  const { data } = await warehouseApi.get<ResponseObject<T>>(path);
  return data.result;
}

/* ── Dashboard ─────────────────────────────────────────────────────────── */
export const useDashboardStats = () =>
  useQuery({
    queryKey: capacityKeys.dashboard,
    queryFn: () => getOne<DashboardStats>('/dashboard/stats'),
    staleTime: 5 * 60 * 1000,
  });

/* ── Suppliers ─────────────────────────────────────────────────────────── */
export const useSuppliers = (params?: ListParams) =>
  useQuery({ queryKey: capacityKeys.suppliers(params), queryFn: () => getList<Supplier>('/suppliers', params) });
export const useSupplier = (id: string) =>
  useQuery({ queryKey: capacityKeys.supplier(id), queryFn: () => getOne<Supplier>(`/suppliers/${id}`), enabled: !!id });

/* ── Item models ───────────────────────────────────────────────────────── */
export const useItemModels = (params?: ListParams) =>
  useQuery({ queryKey: capacityKeys.models(params), queryFn: () => getList<ItemModel>('/item-models', params) });
export const useItemModel = (id: string) =>
  useQuery({ queryKey: capacityKeys.model(id), queryFn: () => getOne<ItemModel>(`/item-models/${id}`), enabled: !!id });

/**
 * The FULL warehouse model catalog across all pages (backend caps page_size at
 * 100). Contract config templates (e.g. Server.VBM) pick models live from the
 * warehouse here instead of a stale bundled snapshot. Returns the raw ItemModel
 * list — callers filter by `category`/`item_type` and read `specifications`.
 */
export const useWarehouseModels = () =>
  useQuery({
    queryKey: ['capacities', 'item-models', 'all'] as const,
    queryFn: async () => {
      const all: ItemModel[] = [];
      let page = 1;
      for (;;) {
        const data = await getList<ItemModel>('/item-models', { page, page_size: 100 });
        all.push(...(data.results ?? []));
        const totalPages = data.pagination?.total_pages ?? 1;
        if (page >= totalPages) break;
        page += 1;
      }
      return all;
    },
    staleTime: 5 * 60 * 1000,
  });

/* ── Locations ─────────────────────────────────────────────────────────── */
export const useLocations = (params?: ListParams) =>
  useQuery({ queryKey: capacityKeys.locations(params), queryFn: () => getList<WarehouseLocation>('/locations', params) });
export const useLocation = (id: string) =>
  useQuery({ queryKey: capacityKeys.location(id), queryFn: () => getOne<WarehouseLocation>(`/locations/${id}`), enabled: !!id });

/* ── Racks ─────────────────────────────────────────────────────────────── */
export const useRacks = (params?: ListParams) =>
  useQuery({ queryKey: capacityKeys.racks(params), queryFn: () => getList<Rack>('/racks', params) });
export const useRack = (id: string) =>
  useQuery({ queryKey: capacityKeys.rack(id), queryFn: () => getOne<Rack>(`/racks/${id}`), enabled: !!id });

/* ── Items ─────────────────────────────────────────────────────────────── */
export const useItems = (params?: ListParams) =>
  useQuery({ queryKey: capacityKeys.items(params), queryFn: () => getList<Item>('/items', params) });
export const useItem = (id: string) =>
  useQuery({ queryKey: capacityKeys.item(id), queryFn: () => getOne<Item>(`/items/${id}`), enabled: !!id });

/* Equipment / Component detail (dedicated endpoints, richer than /items/:id). */
export const useEquipment = (id: string) =>
  useQuery({ queryKey: ['capacities', 'equipment', id], queryFn: () => getOne<Item>(`/equipment/${id}`), enabled: !!id });
export const useComponent = (id: string) =>
  useQuery({ queryKey: ['capacities', 'component', id], queryFn: () => getOne<Item>(`/components/${id}`), enabled: !!id });

/** Items mounted in a rack (for the rack layout view). */
export const useRackItems = (rackId: string) =>
  useQuery({
    queryKey: ['capacities', 'rack-items', rackId],
    queryFn: () => getList<Item>('/items', { rack_id: rackId, page_size: 500 }),
    enabled: !!rackId,
  });

/** Activity/audit log rows are loosely shaped across entities. */
export interface WarehouseActivityLog {
  created_at?: string;
  employee_name?: string;
  action?: string;
  description?: string;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}
/** Entity activity logs: `/equipment|components|racks|locations|processes/:id/logs`. */
export const useEntityLogs = (
  kind: 'equipment' | 'components' | 'racks' | 'locations' | 'processes',
  id: string,
  params?: ListParams,
) =>
  useQuery({
    queryKey: ['capacities', 'logs', kind, id, params],
    queryFn: () => getList<WarehouseActivityLog>(`/${kind}/${id}/logs`, params),
    enabled: !!id,
  });

/* ── Processes ─────────────────────────────────────────────────────────── */
export const useProcesses = (params?: ListParams) =>
  useQuery({ queryKey: capacityKeys.processes(params), queryFn: () => getList<WarehouseProcess>('/processes', params) });
export const useProcess = (id: string) =>
  useQuery({ queryKey: capacityKeys.process(id), queryFn: () => getOne<WarehouseProcess>(`/processes/${id}`), enabled: !!id });
/** Line items grouped by kind (equipment / components / items-to-import). */
export const useProcessItems = (id: string) =>
  useQuery({
    queryKey: ['capacities', 'process-items', id],
    queryFn: () => getOne<ProcessItemsResponse>(`/processes/${id}/items`),
    enabled: !!id,
  });

/* ── Owners (option list) ──────────────────────────────────────────────── */
export const useOwners = () =>
  useQuery({ queryKey: capacityKeys.owners, queryFn: () => getList<OwnerOption>('/owners'), staleTime: 10 * 60 * 1000 });

/* ── Item linked processes (Equipment/Component "Linked Processes" tab) ──── */
export const useItemProcesses = (itemId: string) =>
  useQuery({
    queryKey: ['capacities', 'item-processes', itemId],
    queryFn: () => getList<WarehouseProcess>(`/items/${itemId}/processes`),
    enabled: !!itemId,
  });

/* ── Depreciation (Equipment/Component "Depreciation" tab) ───────────────── */
export interface DepreciationEntry {
  id?: string;
  action?: string;
  previous_value?: number;
  new_value?: number;
  depreciation_rate?: number;
  months?: number;
  reason?: string;
  created_at?: string;
  [key: string]: unknown;
}
export const useDepreciationHistory = (itemId: string) =>
  useQuery({
    queryKey: ['capacities', 'depreciation', itemId],
    queryFn: () => getList<DepreciationEntry>(`/items/${itemId}/depreciation-history`),
    enabled: !!itemId,
    retry: false,
  });

/* ── Location-scoped lists (Location detail tabs) ───────────────────────── */
export const useLocationRacks = (locationId: string) =>
  useQuery({
    queryKey: ['capacities', 'location-racks', locationId],
    queryFn: () => getList<Rack>('/racks', { location_id: locationId, page_size: 500 }),
    enabled: !!locationId,
  });
export const useLocationItems = (locationId: string, params?: ListParams) =>
  useQuery({
    queryKey: ['capacities', 'location-items', locationId, params],
    // Legacy filters items by `location_id` (not `current_location_id`).
    queryFn: () => getList<Item>('/items', { location_id: locationId, ...params }),
    enabled: !!locationId,
  });

/* ── Mutations (forms + process actions). Live execution performed by the
 *    user, not the assistant — these are wired for real use. ─────────────── */
async function post<T>(path: string, body: unknown) {
  const { data } = await warehouseApi.post<ResponseObject<T>>(path, body);
  return data.result;
}
async function put<T>(path: string, body: unknown) {
  const { data } = await warehouseApi.put<ResponseObject<T>>(path, body);
  return data.result;
}
async function del(path: string, body?: unknown) {
  await warehouseApi.delete(path, body ? { data: body } : undefined);
}

const useInvalidate = (prefix: string) => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['capacities', prefix] });
};

/** Generic CRUD mutation factory for a master resource. */
function useMasterMutations<T>(resource: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['capacities'] });
  return {
    create: useMutation({ mutationFn: (body: Partial<T>) => post<T>(`/${resource}`, body), onSuccess: invalidate }),
    update: useMutation({ mutationFn: ({ id, body }: { id: string; body: Partial<T> }) => put<T>(`/${resource}/${id}`, body), onSuccess: invalidate }),
    remove: useMutation({ mutationFn: (id: string) => del(`/${resource}/${id}`), onSuccess: invalidate }),
  };
}
export const useSupplierMutations = () => useMasterMutations<Supplier>('suppliers');
export const useModelMutations = () => useMasterMutations<ItemModel>('item-models');
export const useRackMutations = () => useMasterMutations<Rack>('racks');

/** Toggle active flag (suppliers `/toggle`, models `/toggle-active`). */
export const useToggleActive = (resource: 'suppliers' | 'item-models') => {
  const invalidate = useInvalidate(resource === 'suppliers' ? 'suppliers' : 'item-models');
  const path = resource === 'suppliers' ? 'toggle' : 'toggle-active';
  return useMutation({ mutationFn: (id: string) => post(`/${resource}/${id}/${path}`, {}), onSuccess: invalidate });
};

/** Process approve/reject + create. */
export const useProcessActions = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['capacities'] });
  return {
    approve: useMutation({ mutationFn: ({ id, body }: { id: string; body: unknown }) => post(`/processes/${id}/approve`, body), onSuccess: invalidate }),
    setStatus: useMutation({ mutationFn: ({ id, body }: { id: string; body: unknown }) => put(`/processes/${id}/status`, body), onSuccess: invalidate }),
    create: useMutation({ mutationFn: (body: unknown) => post<WarehouseProcess>('/processes', body), onSuccess: invalidate }),
    update: useMutation({ mutationFn: ({ id, body }: { id: string; body: unknown }) => put<WarehouseProcess>(`/processes/${id}`, body), onSuccess: invalidate }),
  };
};

/** Return components to a warehouse location (Equipment "Return" action). */
export const useReturnComponents = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => post('/components/return', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['capacities'] }),
  });
};

/** Delete a single item (equipment or component). */
export const useDeleteItem = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/items/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['capacities'] }),
  });
};

/** Calculate straight-line depreciation (POST /items/:id/depreciate). */
export interface CalculateDepreciationBody {
  months: number;
  reason?: string;
}
export const useCalculateDepreciation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CalculateDepreciationBody }) =>
      post(`/items/${id}/depreciate`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['capacities'] }),
  });
};

/** Update depreciation settings (PUT /items/:id/depreciation). */
export interface UpdateDepreciationBody {
  depreciation_rate?: number;
  current_value?: number;
  reason?: string;
}
export const useUpdateDepreciation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateDepreciationBody }) =>
      put(`/items/${id}/depreciation`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['capacities'] }),
  });
};
